/**
 * Cluster metrics (+ quantity parsers), ingress, and deployment logs.
 */
import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, unwrap, k8sPath, arr, k8sKeys } from './_internal';
import type { LoadBalancer } from '@/api/opennebula';

// ---- Metrics -------------------------------------------------------------

/** Raw pod-metrics row from `metrics-server` (cpu in n/u/m, mem in Ki/Mi/Gi). */
export interface PodMetric {
  metadata?: { name?: string; namespace?: string; labels?: Record<string, string> };
  timestamp?: string;
  window?: string;
  containers?: { name?: string; usage?: { cpu?: string; memory?: string } }[];
}

/** Raw node-metrics row (usage is a single object, not per-container). */
export interface NodeMetric {
  metadata?: { name?: string; labels?: Record<string, string> };
  timestamp?: string;
  window?: string;
  usage?: { cpu?: string; memory?: string };
}

/** Per-node pod placement + capacity, used for the distribution analytics. */
export interface NodePodDistributionRow {
  node_name: string;
  pod_count: number;
  node_status?: string;
  node_info?: { kubelet_version?: string; container_runtime_version?: string; os_image?: string; architecture?: string };
  allocatable?: { cpu?: string; memory?: string; pods?: string };
  capacity?: { cpu?: string; memory?: string; pods?: string };
}

export interface NodePodDistribution {
  total_pods: number;
  node_count: number;
  distribution: NodePodDistributionRow[];
  summary: { average_pods_per_node: number; max_pods_on_node: number; min_pods_on_node: number };
}

/** Full `/metrics` payload — pods + nodes + per-namespace pods + node↔pod
 *  distribution. Mirrors @console `GetClusterMetricsResponse.data`. */
export interface ClusterMetrics {
  pods: PodMetric[];
  nodes: NodeMetric[];
  namespaces: Record<string, PodMetric[]>;
  node_pod_distribution?: NodePodDistribution;
}

export function useClusterMetrics(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.metrics(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 30_000,
    queryFn: async (): Promise<ClusterMetrics> => {
      const body = unwrap<PodMetric[] | Partial<ClusterMetrics> & { items?: PodMetric[] }>(
        await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/metrics`)),
      );
      // Tolerate the legacy array shape (pods only) as well as the full object.
      if (Array.isArray(body)) return { pods: body, nodes: [], namespaces: {} };
      return {
        pods: body.pods ?? body.items ?? [],
        nodes: body.nodes ?? [],
        namespaces: body.namespaces ?? {},
        node_pod_distribution: body.node_pod_distribution,
      };
    },
  });
}

/** Per-node-pool metrics (`nodepools/:pid/metrics`). Raw shape is backend-defined
 *  (node usage rows); returned as-is for the pool-detail analytics view. */
export function useNodePoolMetrics(serviceId: string | undefined, clusterId: string | undefined, poolId: string | undefined) {
  return useQuery({
    queryKey: ['k8s', serviceId ?? '', 'clusters', clusterId ?? '', 'pools', poolId ?? '', 'metrics'] as const,
    enabled: !!serviceId && !!clusterId && !!poolId,
    retry: false,
    refetchInterval: 30_000,
    queryFn: async () => unwrap<unknown>(await serviceApi.get(k8sPath(serviceId!, `clusters/${clusterId}/nodepools/${poolId}/metrics`))),
  });
}

/** Parse a Kubernetes CPU quantity → millicores. (1234567890n → 1234m). */
export function cpuToMillicores(cpu?: string): number {
  if (!cpu) return 0;
  const m = /^([\d.]+)([a-zµ]*)$/.exec(cpu.trim());
  if (!m) return 0;
  const n = parseFloat(m[1]);
  switch (m[2]) {
    case 'n': return n / 1e6;
    case 'u': case 'µ': return n / 1e3;
    case 'm': return n;
    case '': return n * 1000;
    default: return n;
  }
}
/** Parse a Kubernetes memory quantity → MiB. */
export function memToMiB(mem?: string): number {
  if (!mem) return 0;
  const m = /^([\d.]+)([A-Za-z]*)$/.exec(mem.trim());
  if (!m) return 0;
  const n = parseFloat(m[1]);
  switch (m[2]) {
    case 'Ki': return n / 1024;
    case 'Mi': return n;
    case 'Gi': return n * 1024;
    case 'Ti': return n * 1024 * 1024;
    case 'k': return n / 1000 / 1.048576;
    case 'M': return n / 1.048576;
    case 'G': return (n * 1000) / 1.048576;
    default: return n / (1024 * 1024);
  }
}

// ---- Ingress -------------------------------------------------------------

export interface Ingress {
  name?: string;
  host?: string;
  service?: string;
  ports?: string;
}

export function useClusterIngress(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.ingress(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => arr<Ingress>(unwrap(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/ingress`))), 'ingress'),
  });
}

/** Ingress controller detail: `{ ingress: <k8s Service>, lb, stats }`. */
export interface IngressService {
  metadata?: { name?: string; namespace?: string };
  spec?: { type?: string; clusterIP?: string; ports?: { name?: string; port?: number; nodePort?: number; protocol?: string; targetPort?: string | number }[] };
  status?: { loadBalancer?: { ingress?: { ip?: string; hostname?: string }[] } };
}
/** A single entry in HAProxy stats `values[]`. title is 'Backend' or 'Server'. */
export interface HaProxyStatValue {
  title?: string;
  server_name?: string;
  status?: string;
  session_total?: number;
  session_current?: number;
  session_max?: number;
  byte_in?: number;
  byte_out?: number;
}

/** Real HAProxy stats shape: array of `{proxy_name, values[]}` objects.
 *  `values[]` contains Backend + Server entries (`title` field distinguishes). */
export interface HaProxyStatGroup {
  proxy_name?: string;
  values?: HaProxyStatValue[];
}

export interface IngressDetail {
  ingress?: IngressService;
  /** The backing compute (s10003) LB instance that fronts the ingress controller. */
  lb?: LoadBalancer;
  /** Compute service uuid that owns `lb` — used to drive the s10003 cold-resize. */
  compute_service_uuid?: string;
  /** Desired ingress LB flavor recorded on the cluster spec (survives recreate). */
  ingress_lb_flavor?: string;
  /** Real shape: array of HaProxy stat groups `{proxy_name, values[]}`. */
  stats?: HaProxyStatGroup[] | null;
}
export function useClusterIngressDetail(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: [...k8sKeys.ingress(serviceId ?? '', id ?? ''), 'detail'] as const,
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async (): Promise<IngressDetail> => {
      const raw = unwrap<IngressDetail & { stats?: unknown }>(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/ingress`)));
      if (!raw) return {};
      // Normalise stats: backend returns array or single object; skip lb_stats entries.
      const statsRaw = raw.stats;
      let stats: HaProxyStatGroup[] | null = null;
      if (Array.isArray(statsRaw)) stats = statsRaw as HaProxyStatGroup[];
      else if (statsRaw && typeof statsRaw === 'object') stats = [statsRaw as HaProxyStatGroup];
      return { ...raw, stats };
    },
    // Master health + LB IP on the overview derive from this endpoint, so poll
    // while the control plane is still coming up (no stats yet, or any master
    // server not "UP" behind HAProxy) and stop once every master reports UP —
    // mirrors useClusterNodes so the masters/ingress refresh live without a reload.
    refetchInterval: (q) => {
      const servers = (q.state.data?.stats ?? [])
        .flatMap((g) => g.values ?? [])
        .filter((v) => v.server_name !== 'BACKEND');
      const allUp = servers.length > 0 && servers.every((v) => /up/i.test(v.status ?? ''));
      return allUp ? false : 15_000;
    },
  });
}

/**
 * Record the desired ingress LB flavor on the cluster spec so a resize survives an
 * ingress-controller recreate. This is the desired-state + audit anchor only; the
 * actual cold-resize of the VRouter VM runs against the compute (s10003) endpoints.
 */
export function useResizeIngressLbFlavor(
  serviceId: string,
  clusterId: string,
  options?: UseMutationOptions<unknown, unknown, { flavor: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { flavor: string }) =>
      (await serviceApi.put(k8sPath(serviceId, `clusters/${clusterId}/ingress/lb`), p)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: k8sKeys.ingress(serviceId, clusterId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Ingress gateway pinning --------------------------------------------

/** A pinnable ingress LB — one OpenNebula HAProxy VM per ingress-controller Service
 *  of type LoadBalancer. Pinning one makes the console's Statistics/Monitoring/Resize
 *  track it (the data source is that LB, not the k8s Service). */
export interface IngressGateway {
  id: string;
  name: string;
  status?: string;
  flavor?: string | null;
  cores?: number | null;
  memory?: number | null;
  created_at?: string;
  /** The managed nginx add-on LB (name matches ingress-nginx-controller). */
  managed?: boolean;
  selected?: boolean;
}

export interface IngressGatewayList {
  gateways: IngressGateway[];
  /** Currently-pinned LB id (cluster spec `metadata.ingress`). */
  selected?: string | null;
}

/** List the cluster's ingress LBs the customer can pin as the console target. */
export function useClusterIngressGateways(serviceId: string | undefined, id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...k8sKeys.ingress(serviceId ?? '', id ?? ''), 'gateways'] as const,
    enabled: !!serviceId && !!id && enabled,
    retry: false,
    queryFn: async (): Promise<IngressGatewayList> => {
      const raw = unwrap<IngressGatewayList>(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/ingress/gateways`)));
      return raw ?? { gateways: [] };
    },
  });
}

/** Pin which ingress LB the console tracks (records its id on the cluster spec). */
export function usePinIngressGateway(
  serviceId: string,
  clusterId: string,
  options?: UseMutationOptions<unknown, unknown, { lb_id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { lb_id: string }) =>
      (await serviceApi.put(k8sPath(serviceId, `clusters/${clusterId}/ingress/gateway`), p)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: k8sKeys.ingress(serviceId, clusterId) });
      options?.onSuccess?.(...a);
    },
  });
}

// ---- Logs ----------------------------------------------------------------

/** Full logs response shape. Consumers read `.logs` for text; `.available_nodes`
 *  and `.available_sources` drive the node/source dropdowns. */
export interface ClusterLogsPayload {
  logs: string;
  available_nodes?: string[];
  available_sources?: string[];
  node?: string;
  log_source?: string;
}

export function useClusterLogs(
  serviceId: string | undefined,
  id: string | undefined,
  opts: { node?: string; service?: string; lines?: number } = {},
) {
  // Default node = `provisioner` (where deployment/cloud-init logs live).
  const { node = 'provisioner', service = 'deployment', lines = 200 } = opts;
  return useQuery({
    // `lines` MUST be part of the key — otherwise changing the line-count selector
    // returns the cached response and the log never re-truncates/re-fetches.
    queryKey: [...k8sKeys.logs(serviceId ?? '', id ?? '', node, service), lines],
    enabled: !!serviceId && !!id,
    retry: false,
    refetchInterval: 15_000,
    queryFn: async (): Promise<ClusterLogsPayload> => {
      const params: Record<string, unknown> = { lines, node, service };
      const body = unwrap<ClusterLogsPayload | string>(
        await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/deployments/logs`), { params }),
      );
      if (typeof body === 'string') return { logs: body };
      return { logs: body.logs ?? '', available_nodes: body.available_nodes, available_sources: body.available_sources, node: body.node, log_source: body.log_source };
    },
  });
}
